/**
 * FactoryItemListView — card "Item List" trong Factory Data hub (/<code>/data).
 * Hiển thị bảng `public.list_and_valo` lấy từ KHO References canonical
 * (canonical key #6 `list-and-valo`) — CÙNG 1 URL `GET /references/list-and-valo`
 * mà Inbound/Outbound/Purchase/Sale Contract đang dùng; layout cột per-user cũng
 * share chung key `references.list-and-valo`. list_and_valo là master data dùng
 * chung, không scope theo factory.
 *
 * Read-only (như references). Header strip Back "Datasets" + accent pill khớp
 * bố cục các embed khác trong hub (SContractTab/deduction-rate).
 */
import { useMemo, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import { Box, Button, Stack, Typography, alpha } from '@mui/material'

import { filterBarBtnSx } from '@/components/dataList'
import { REFERENCE_CATALOG } from '@/components/references/catalog'
import ReferenceGridBase from '@/components/references/ReferenceGridBase'
import type { ReferenceMenuColumn } from '@/components/references/types'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { useGetProcurementReferenceRowsQuery } from '@/store/api/procurementApi'

// Canonical key #6 trong KHO References.
const PAGE_KEY = 'list-and-valo' as const
// Accent = màu card 'item-list' trong FACTORY_DATA_HUB_META.
const ACCENT = '#145a59'

export default function FactoryItemListView({ onBackToHub }: { onBackToHub?: () => void }) {
  const [search, setSearch] = useState('')
  // Canonical config, nhưng ép MỖI cột có `width` cụ thể (flex/minWidth → width)
  // để grid dùng fixed table-layout: cột wrap (description/note…) không phải
  // tính lại intrinsic-width theo mọi row khi scroll → hết giật lag. Cặp với
  // `fallbackToConfigWidth` bên dưới để width config có hiệu lực khi chưa resize.
  const page = useMemo(() => {
    const base = REFERENCE_CATALOG[PAGE_KEY]
    return {
      ...base,
      columns: base.columns.map((c) => ({ ...c, width: c.width ?? c.minWidth ?? 200 })),
    }
  }, [])

  const { currentData, isLoading, isFetching, error, refetch } = useGetProcurementReferenceRowsQuery(
    { pageKey: PAGE_KEY, limit: 1_000_000 },
    { refetchOnMountOrArgChange: true },
  )

  const menuColumns = useMemo<ReferenceMenuColumn[]>(
    () => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  // Column layout keyed theo canonical page-key (share xuyên function, per-user).
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `references.${PAGE_KEY}`,
  })
  const widths = useColumnWidths(`references.${PAGE_KEY}`)

  const rows = currentData?.items ?? []

  // Back "Datasets" + accent pill — nhúng TRONG FilterBar (1 thanh tích hợp,
  // bố cục giống tab /baoloc/consumables), không phải thanh header riêng.
  const headerSlot = onBackToHub ? (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBackToHub}
        sx={filterBarBtnSx}
      >
        Datasets
      </Button>
      <Stack
        direction="row"
        spacing={0.625}
        alignItems="center"
        sx={{
          mx: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: ACCENT,
          color: '#fff',
          boxShadow: `0 1px 3px ${alpha(ACCENT, 0.45)}`,
        }}
      >
        <Inventory2Icon sx={{ fontSize: '0.95rem', color: '#fff' }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            fontSize: '0.85rem',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            whiteSpace: 'nowrap',
            lineHeight: 1.1,
          }}
        >
          Item List
        </Typography>
      </Stack>
    </>
  ) : undefined

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ReferenceGridBase
        page={page}
        rows={rows}
        search={search}
        onSearchChange={setSearch}
        loading={isLoading}
        fetching={isFetching}
        error={error}
        onRetry={() => void refetch()}
        fillHeight
        columnsCtrl={columnsCtrl}
        widths={widths}
        totalsMode="sum-flag"
        fallbackToConfigWidth
        headerSlot={headerSlot}
      />
    </Box>
  )
}
